// frontend/src/components/GameCard.jsx
import { useState } from 'react';
import { TEAM_LOGOS } from '../utils/teamLogos';
import { getTeamLogoUrl, getEsportsTeamLogoUrl } from '../api';
import { splitLeagueName } from '../utils/homeTabHelpers';
import { colors } from '../theme';
import LiveDetailsModal from './LiveDetailsModal';

// odds-api.io's clock.statusDetail is already sport-appropriate display text
// ("2nd half", "3rd quarter", "Map 2", "Started", ...). Append the running
// clock minute for the sports that report one (football, basketball, ...).
function formatClock(clock) {
  if (!clock) return null;
  const parts = [];
  if (clock.statusDetail) parts.push(clock.statusDetail);
  if (clock.running && typeof clock.minute === 'number') parts.push(`${clock.minute}'`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export default function GameCard({ game, onAddToBetSlip, onLoadProps, betSlip, favoriteTeams, onToggleFavorite, isInBetSlip: isInBetSlipOverride }) {
  // game.status comes from the normalized pipeline (works for both the
  // prematch REST feed and the live WebSocket feed); game.is_live is an
  // extra flag /live-games sets explicitly — either is enough.
  const isLive = game.status === 'live' || game.is_live;
  const [showLiveDetails, setShowLiveDetails] = useState(false);

  const homeScore = game.home_score ?? null;
  const awayScore = game.away_score ?? null;
  const clockText = formatClock(game.clock);

  const gameStarted = game.commence_time ? new Date(game.commence_time) <= new Date() : false;
  const canBet = !gameStarted || isLive;

  const getMarketOdds = (marketKey) => {
    const bookmaker = game.bookmakers?.[0];
    if (!bookmaker) return null;
    return bookmaker.markets?.find(m => m.key === marketKey) || null;
  };

  const isInBetSlip = (market, outcome) => {
    if (isInBetSlipOverride) return isInBetSlipOverride(market, outcome);
    return betSlip.some(bet =>
      bet.game_id === game.id &&
      bet.market_type === market.key &&
      bet.selection === outcome.name
    );
  };

  const isValidOdds = (price) => {
    if (typeof price !== 'number') return false;
    if (!isFinite(price)) return false;
    if (price < 1.01) return false;
    if (price > 10000) return false;
    return true;
  };

  const formatOdds = (price) => {
    if (typeof price === 'string') return price;
    if (typeof price === 'number') {
      if (!isValidOdds(price)) return null;
      if (price >= 2.0) {
        return `+${Math.round((price - 1) * 100)}`;
      } else {
        return `${Math.round(-100 / (price - 1))}`;
      }
    }
    return price;
  };

  const filterValidOutcomes = (outcomes) => {
    if (!outcomes) return [];
    return outcomes.filter(o => isValidOdds(o.price));
  };

  const h2hRaw = getMarketOdds('h2h_3way') || getMarketOdds('h2h');
  const spreadsRaw = getMarketOdds('spreads') || getMarketOdds('spreads_maps');
  const totalsRaw = getMarketOdds('totals') || getMarketOdds('totals_maps');

  const h2hFiltered = h2hRaw ? filterValidOutcomes(h2hRaw.outcomes) : [];
  const spreadsFiltered = spreadsRaw ? filterValidOutcomes(spreadsRaw.outcomes) : [];
  const totalsFiltered = totalsRaw ? filterValidOutcomes(totalsRaw.outcomes) : [];

  const h2h = h2hFiltered.length > 0 ? { ...h2hRaw, outcomes: h2hFiltered } : null;
  const spreads = spreadsFiltered.length > 0 ? { ...spreadsRaw, outcomes: spreadsFiltered } : null;
  const totals = totalsFiltered.length > 0 ? { ...totalsRaw, outcomes: totalsFiltered } : null;

  const isFavoriteHome = favoriteTeams?.includes(game.home_team);
  const isFavoriteAway = favoriteTeams?.includes(game.away_team);

  // odds-api's own crest (cached server-side, keyed by participant id) has far
  // better coverage than the hardcoded TEAM_LOGOS list — prefer it whenever we
  // have an id. Esports is the one exception: odds-api has zero logo coverage
  // there (checked live), so those go straight to the Liquipedia-backed
  // lookup instead, keyed by (game title, team name) since Liquipedia has no
  // participant-id system — the game title is the same prefix that already
  // powers the esports league grouping (splitLeagueName). Any of these can
  // still come back empty, so <img onError> hides it either way.
  const isEsports = game.sport_key === 'esports_all';
  const esportsGameTitle = isEsports ? splitLeagueName(game.league || '').group : null;
  const awayLogo = isEsports
    ? getEsportsTeamLogoUrl(esportsGameTitle, game.away_team)
    : getTeamLogoUrl(game.away_id) || TEAM_LOGOS[game.away_team];
  const homeLogo = isEsports
    ? getEsportsTeamLogoUrl(esportsGameTitle, game.home_team)
    : getTeamLogoUrl(game.home_id) || TEAM_LOGOS[game.home_team];

  const teamNick = (name) => (name || '').toLowerCase().split(' ').pop();
  const matchesTeam = (outcomeName, teamName) => {
    if (!outcomeName || !teamName) return false;
    const o = outcomeName.toLowerCase();
    const t = teamName.toLowerCase();
    return o === t || (teamNick(o) === teamNick(t) && teamNick(t).length > 1);
  };
  // Home first, then draw, then away — matches the new layout's home-on-top row order.
  const sortOutcomes = (outcomes) => {
    if (!outcomes || outcomes.length < 2) return outcomes || [];
    const away = outcomes.find(o => matchesTeam(o.name, game.away_team));
    const home = outcomes.find(o => matchesTeam(o.name, game.home_team));
    const draw = outcomes.find(o => /draw/i.test(o.name));
    if (away && home) return draw ? [home, draw, away] : [home, away];
    return outcomes;
  };

  const isMLOnly = h2h && !spreads && !totals;
  const is3WayML = h2h && (h2h.key === 'h2h_3way' || h2h.outcomes.length === 3);
  const spreadsLabel = spreads?.key === 'spreads_maps' ? 'Maps Hdp' : 'Spread';
  const totalsLabel = totals?.key === 'totals_maps' ? 'Maps' : 'Total';

  const pillStyle = (selected) => ({
    background: selected ? colors.cardBgActive : colors.sectionBg,
    border: selected ? `1px solid ${colors.gold}` : 'none',
    color: colors.textPrimary,
  });

  const renderOddsPill = (market, outcome, key, topLine) => (
    <button
      key={key}
      onClick={() => onAddToBetSlip(game, market, outcome)}
      className="w-full overflow-hidden flex-1 rounded-lg px-2 py-0.5 md:py-1.5 min-h-[40px] md:min-h-[56px] flex flex-col items-center justify-center gap-0.5 transition active:scale-95"
      style={pillStyle(isInBetSlip(market, outcome))}
    >
      {topLine && <div className="text-[12px] md:text-[16px] font-bold leading-tight whitespace-nowrap" style={{ color: colors.textPrimary }}>{topLine}</div>}
      <div className="text-[12px] md:text-[16px] font-bold leading-tight whitespace-nowrap" style={{ color: isInBetSlip(market, outcome) ? colors.textPrimary : colors.odds }}>
        {formatOdds(outcome.price)}
      </div>
    </button>
  );

  // `score` is that specific team's own score — putting it right next to
  // their name (rather than a bare "84-79" elsewhere) is what actually makes
  // it unambiguous which number belongs to which team.
  const teamRow = (teamName, logo, isFav, onToggle, score) => (
    <div className="flex items-center gap-2 min-w-0">
      <button onClick={onToggle} className="hover:scale-110 transition flex-shrink-0 text-base" style={{ color: colors.gold }}>
        {isFav ? '⭐' : '☆'}
      </button>
      {logo && (
        <img
          src={logo}
          alt={teamName}
          className="w-7 h-7 md:w-9 md:h-9 object-contain flex-shrink-0"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      )}
      <span className="text-[13px] md:text-[19px] font-semibold leading-[1.1] md:leading-tight truncate" style={{ color: colors.textPrimary }}>
        {teamName}
      </span>
      {score != null && (
        <span
          className="text-[13px] md:text-[16px] font-bold flex-shrink-0 ml-auto px-2 py-0.5 rounded-md"
          style={{ color: colors.textPrimary, background: colors.sectionBg, fontVariantNumeric: 'tabular-nums' }}
        >
          {score}
        </span>
      )}
    </div>
  );

  const dateTimeBlock = (
    <div className="text-[15px] md:text-[18px] flex items-center gap-1 min-w-0" style={{ color: colors.textPrimary }}>
      {isLive ? (
        <>
          <span className="flex items-center gap-1 font-semibold flex-shrink-0" style={{ color: colors.live }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.live }}></span>
            LIVE
          </span>
          {/* Each team's score now sits on its own row below — a bare
              "84-79" up here has no label for which number is which team. */}
          {clockText && (
            <span className="text-[12px] md:text-[14px] font-normal truncate" style={{ color: colors.pending }}>{clockText}</span>
          )}
        </>
      ) : gameStarted ? (
        <span className="font-medium" style={{ color: colors.pending }}>STARTED</span>
      ) : game.commence_time ? (
        <span>{new Date(game.commence_time).toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
      ) : (
        <span style={{ color: colors.textFaint }}>Date TBD</span>
      )}
    </div>
  );

  // Props never live-update (only ML/spread/totals do), and the backend
  // already refuses to serve them for a live game — so the button would open
  // an always-empty modal. Pregame only.
  const propsButton = canBet && !isLive && (game.player_props_available || game.game_props_available) ? (
    <button
      onClick={() => onLoadProps(game)}
      className="text-[13px] md:text-[16px] px-1.5 py-0.5 md:px-2.5 md:py-1 rounded-lg font-medium transition flex-shrink-0 min-h-[30px] md:min-h-0 flex items-center justify-center"
      style={{ background: colors.sectionBg, color: colors.textPrimary }}
    >
      Props
    </button>
  ) : null;

  // Takes Props' place in the header row for live games — same slot, since
  // the two are mutually exclusive (props only shows pregame).
  const detailsButton = isLive && game.period_scores && Object.keys(game.period_scores).length > 0 ? (
    <button
      onClick={() => setShowLiveDetails(true)}
      className="text-[13px] md:text-[16px] px-1.5 py-0.5 md:px-2.5 md:py-1 rounded-lg font-medium transition flex-shrink-0 min-h-[30px] md:min-h-0 flex items-center justify-center"
      style={{ background: colors.sectionBg, color: colors.textPrimary }}
    >
      Details
    </button>
  ) : null;

  return (
    <>
    <div className="rounded-xl p-2 md:p-2.5" style={{ background: colors.cardBg, boxShadow: colors.shadowCard, color: colors.textPrimary }}>
      {!canBet ? (
        <>
          <div className="flex items-center gap-1.5 md:gap-3 mb-0.5">
            <div className="flex-1 min-w-0">{dateTimeBlock}</div>
            {propsButton}
            {detailsButton}
          </div>
          <div className="text-center py-3 text-sm rounded-lg" style={{ color: colors.textFaint, background: colors.sectionBg }}>
            Betting closed
          </div>
        </>
      ) : isMLOnly ? (
        <div>
          <div className="flex items-center gap-1.5 md:gap-3 mb-1">
            <div className="flex-1 min-w-0">{dateTimeBlock}</div>
            {propsButton}
            {detailsButton}
            {!is3WayML && (
              <div className="w-20 md:w-24 flex-shrink-0 text-center text-[12px] md:text-[16px] font-semibold" style={{ color: colors.textPrimary }}>Moneyline</div>
            )}
          </div>
          <div className="flex flex-col gap-1 md:gap-1.5">
            {sortOutcomes(h2h.outcomes).map((outcome, idx) => {
              const teamName = matchesTeam(outcome.name, game.home_team) ? game.home_team
                              : matchesTeam(outcome.name, game.away_team) ? game.away_team
                              : outcome.name;
              const logo = matchesTeam(outcome.name, game.home_team) ? homeLogo
                          : matchesTeam(outcome.name, game.away_team) ? awayLogo
                          : null;
              const isFav = matchesTeam(outcome.name, game.home_team) ? isFavoriteHome
                           : matchesTeam(outcome.name, game.away_team) ? isFavoriteAway
                           : false;
              const teamScore = matchesTeam(outcome.name, game.home_team) ? homeScore
                               : matchesTeam(outcome.name, game.away_team) ? awayScore
                               : null;
              const onToggle = () => onToggleFavorite(teamName);
              return (
                <div key={idx} className="flex items-center gap-2 md:gap-3">
                  <div className="flex-1 min-w-0">{teamRow(teamName, logo, isFav, onToggle, isLive ? teamScore : null)}</div>
                  <div className="w-20 md:w-24 flex-shrink-0">{renderOddsPill(h2h, outcome, idx, is3WayML && /draw/i.test(outcome.name) ? 'Draw' : null)}</div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          {/* Status + column labels merged row */}
          <div className="flex items-center gap-1.5 md:gap-3">
            <div className="flex-1 min-w-0">{dateTimeBlock}</div>
            {propsButton}
            {detailsButton}
            {h2h && <div className="w-14 md:w-20 flex-shrink-0 text-center text-[12px] md:text-[16px] font-semibold" style={{ color: colors.textPrimary }}>Moneyline</div>}
            {spreads && <div className="w-14 md:w-20 flex-shrink-0 text-center text-[12px] md:text-[16px] font-semibold" style={{ color: colors.textPrimary }}>{spreadsLabel}</div>}
            {totals && <div className="w-14 md:w-20 flex-shrink-0 text-center text-[12px] md:text-[16px] font-semibold" style={{ color: colors.textPrimary }}>{totalsLabel}</div>}
          </div>

          {[
            { team: game.home_team, logo: homeLogo, isFav: isFavoriteHome, row: 0, score: homeScore },
            { team: game.away_team, logo: awayLogo, isFav: isFavoriteAway, row: 1, score: awayScore },
          ].map(({ team, logo, isFav, row, score }) => {
            const h2hOutcome = h2h && sortOutcomes(h2h.outcomes.filter(o => !/draw/i.test(o.name)))[row];
            const spreadOutcome = spreads && sortOutcomes(spreads.outcomes.filter(o => !/draw/i.test(o.name)))[row];
            // Totals aren't team-specific — Over aligns to the top (home) row, Under to the bottom (away) row.
            const totalOutcome = totals && totals.outcomes[row];
            return (
              <div key={team} className="flex items-center gap-1.5 md:gap-3">
                <div className="flex-1 min-w-0">{teamRow(team, logo, isFav, () => onToggleFavorite(team), isLive ? score : null)}</div>
                {h2h && <div className="w-14 md:w-20 flex-shrink-0">{h2hOutcome ? renderOddsPill(h2h, h2hOutcome, `h2h-${row}`) : <div className="h-[40px] md:h-14" />}</div>}
                {spreads && (
                  <div className="w-14 md:w-20 flex-shrink-0">
                    {spreadOutcome ? renderOddsPill(spreads, spreadOutcome, `sp-${row}`, `${spreadOutcome.point > 0 ? '+' : ''}${spreadOutcome.point}`) : <div className="h-[40px] md:h-14" />}
                  </div>
                )}
                {totals && (
                  <div className="w-14 md:w-20 flex-shrink-0">
                    {totalOutcome ? renderOddsPill(totals, totalOutcome, `tot-${row}`, `${totalOutcome.name.charAt(0)} ${totalOutcome.point}`) : <div className="h-[40px] md:h-14" />}
                  </div>
                )}
              </div>
            );
          })}

          {is3WayML && (() => {
            const drawOutcome = h2h.outcomes.find(o => /draw/i.test(o.name));
            if (!drawOutcome) return null;
            return (
              <div className="flex items-center gap-1.5 md:gap-3">
                <div className="flex-1 min-w-0">
                  <span className="text-[13px] md:text-[19px] font-semibold leading-[1.1] md:leading-tight" style={{ color: colors.textPrimary }}>Draw</span>
                </div>
                <div className="w-14 md:w-20 flex-shrink-0">{renderOddsPill(h2h, drawOutcome, 'draw')}</div>
                {spreads && <div className="w-14 md:w-20 flex-shrink-0" />}
                {totals && <div className="w-14 md:w-20 flex-shrink-0" />}
              </div>
            );
          })()}
        </div>
      )}
    </div>
    {showLiveDetails && <LiveDetailsModal game={game} onClose={() => setShowLiveDetails(false)} />}
    </>
  );
}
